import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Html5QrcodeScanner } from 'html5-qrcode';
import StudentLayout from '../components/StudentLayout';
import theme from '../styles/theme';

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 20px;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: #6b7280;
  font-size: 14px;
`;

// html5-qrcode renders its own camera-permission prompts, the scan region, a
// torch/zoom toggle where supported, and a "choose file" fallback. The
// library explicitly documents its element ids/classes as the supported
// theming hook (PublicUiElementIdAndClasses in its own source) -- this reskins
// that default plain widget to match the rest of MYC's rounded, indigo look
// instead of leaving its bare-HTML appearance.
const ScannerHost = styled.div`
  #myc-qr-reader {
    border: none !important;
    font-family: inherit;
  }

  #myc-qr-reader__header_message {
    border: none !important;
    padding: 0 0 12px !important;
    color: #6b7280 !important;
    font-size: 13px !important;
  }

  /* The library's own "powered by" attribution icon -- not useful to
     students, just visual clutter in the corner of the scan region. */
  #myc-qr-reader__header_message img[alt="Info icon"] {
    display: none !important;
  }

  #myc-qr-reader__scan_region {
    border-radius: 14px;
    overflow: hidden;
    background: #f8fafc;
    border: 1px solid ${theme.primarySoftBorder};
    padding: 0 !important;
  }

  #myc-qr-reader__scan_region img {
    opacity: 0.35;
    padding: 32px 0;
  }

  video {
    border-radius: 14px;
  }

  #myc-qr-reader__dashboard {
    padding: 16px 4px 0;
  }

  #myc-qr-reader__dashboard_section_csr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  /* Base class the library applies to every major interactive element
     (buttons, the camera <select>, the file-selection button). */
  .html5-qrcode-element {
    font-family: inherit !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    border: 1px solid ${theme.primaryLightBorder} !important;
    background: #ffffff !important;
    color: ${theme.primaryDark} !important;
    padding: 10px 20px !important;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }

  .html5-qrcode-element:hover {
    background: ${theme.primaryLight} !important;
  }

  /* Primary calls to action -- request permission / start scanning. */
  #html5-qrcode-button-camera-permission,
  #html5-qrcode-button-camera-start {
    background: ${theme.gradient} !important;
    border: none !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(67, 56, 202, 0.28);
  }

  #html5-qrcode-button-camera-permission:hover,
  #html5-qrcode-button-camera-start:hover {
    filter: brightness(1.05);
  }

  #html5-qrcode-button-camera-stop {
    background: #ffffff !important;
    border: 1px solid #fecaca !important;
    color: #dc2626 !important;
  }

  #html5-qrcode-button-camera-stop:hover {
    background: #fef2f2 !important;
  }

  #html5-qrcode-select-camera {
    padding: 8px 12px !important;
  }

  /* Zoom slider -- only rendered when the active camera reports zoom
     support (showZoomSliderIfSupported below); overrides the generic
     button-shaped .html5-qrcode-element box styling above, which doesn't
     suit a <input type="range">. */
  #html5-qrcode-input-range-zoom {
    -webkit-appearance: none;
    appearance: none;
    width: 55% !important;
    height: 4px !important;
    border-radius: 999px !important;
    border: none !important;
    padding: 0 !important;
    background: ${theme.primaryLightBorder} !important;
    opacity: 1 !important;
    cursor: pointer;
  }

  #html5-qrcode-input-range-zoom::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${theme.primary};
    box-shadow: 0 1px 4px rgba(67, 56, 202, 0.4);
    cursor: pointer;
  }

  #html5-qrcode-input-range-zoom::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: ${theme.primary};
    box-shadow: 0 1px 4px rgba(67, 56, 202, 0.4);
    cursor: pointer;
  }

  #html5-qrcode-input-range-zoom + span {
    font-size: 13px !important;
    font-weight: 600;
    color: ${theme.primaryDark};
  }

  /* "Scan an Image File" / "Scan Using Camera" swap link. */
  #html5-qrcode-anchor-scan-type-change {
    display: inline-block;
    margin-top: 4px;
    border: none !important;
    background: none !important;
    color: ${theme.primaryDark} !important;
    font-weight: 600 !important;
    text-decoration: underline !important;
    padding: 4px !important;
  }

  #html5-qrcode-anchor-scan-type-change:hover {
    color: ${theme.primaryDarker} !important;
  }
`;

const NotFoundText = styled.p`
  margin-top: 12px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
`;

const SCANNER_ELEMENT_ID = 'myc-qr-reader';
const TOKEN_PATTERN = /\/app\/scan-attendance\/([A-Za-z0-9]+)/;

export default function ScanAttendanceEntry() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const [notRecognized, setNotRecognized] = useState(false);

  useEffect(() => {
    const scanner = new Html5QrcodeScanner(
      SCANNER_ELEMENT_ID,
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        rememberLastUsedCamera: true,
        // Shows a zoom slider under the camera controls when the active
        // camera reports zoom support (most phone cameras do) -- lets
        // students zoom in on a small/distant QR code instead of just
        // physically moving the phone closer.
        showZoomSliderIfSupported: true,
        defaultZoomValueIfSupported: 1,
      },
      /* verbose= */ false
    );
    scannerRef.current = scanner;

    const handleSuccess = (decodedText) => {
      if (hasNavigatedRef.current) return;
      const match = decodedText.match(TOKEN_PATTERN);
      if (!match) {
        // Not one of our attendance QR codes -- keep the camera running so
        // they can try again instead of dead-ending on an error screen.
        setNotRecognized(true);
        return;
      }
      hasNavigatedRef.current = true;
      scanner.clear().catch(() => {});
      navigate(`/app/scan-attendance/${match[1]}`);
    };

    // html5-qrcode calls this continuously while no QR is in frame -- that's
    // normal scanning noise, not a real error, so it's intentionally ignored.
    const handleScanFailure = () => {};

    scanner.render(handleSuccess, handleScanFailure);

    return () => {
      scannerRef.current?.clear().catch(() => {});
    };
  }, [navigate]);

  return (
    <StudentLayout title="Scan Attendance">
      <Wrapper>
        <Section>
          <Title>Scan Attendance QR</Title>
          <Subtitle>Point your camera at the QR code your lecturer is displaying to mark your attendance.</Subtitle>
          <ScannerHost id={SCANNER_ELEMENT_ID} />
          {notRecognized && (
            <NotFoundText>That doesn't look like an attendance QR code -- keep scanning.</NotFoundText>
          )}
        </Section>
      </Wrapper>
    </StudentLayout>
  );
}
